import { createContext, useCallback, useContext, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Snackbar } from "@mui/material";
import { useAuth } from "../../context/authContext";
import { crmLevel, hasCrm } from "../../access";
import crmService from "../../api/crmService";
import userService from "../../api/userService";
import { emptyPending, normalizePending, NOTIFICATION_REFRESH_EVENT, requestCrmDataRefresh, requestNotificationRefresh } from "../../utils/notificationCenter";
import { buildDayQueue, todayKey } from "../../pages/CRM/dayPlan";

const CallDesk = lazy(() => import("../../pages/CRM/components/CallDesk"));

const DayCoachContext = createContext(null);

function progressStorageKey(userId) {
  return `trazia-day-progress:${userId}`;
}

function readProgress(userId) {
  const empty = { doneIds: [], skippedIds: [], doneCount: 0 };
  if (!userId) return empty;
  try {
    const raw = JSON.parse(sessionStorage.getItem(progressStorageKey(userId)) || "null");
    if (!raw || raw.date !== todayKey()) return empty;
    const doneIds = Array.isArray(raw.doneIds) ? raw.doneIds : [];
    return {
      doneIds,
      skippedIds: Array.isArray(raw.skippedIds) ? raw.skippedIds : [],
      doneCount: Number(raw.doneCount) || doneIds.length,
    };
  } catch {
    return empty;
  }
}

export function DayCoachProvider({ children }) {
  const { user, updateUser } = useAuth();
  // El marketer no trabaja el "diario": sin day coach ni brief.
  const isMarketer = crmLevel(user) === "marketer";
  const enabled = Boolean(user && hasCrm(user)) && !isMarketer;
  const isSeller = user?.account === "member" && Boolean(user?.grants?.crm) && !isMarketer;
  const canReset = user?.account === "admin";
  const userId = user?._id || user?.id || "";

  const [pending, setPending] = useState(emptyPending);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booted, setBooted] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [doneIds, setDoneIds] = useState(() => readProgress(userId).doneIds);
  const [skippedIds, setSkippedIds] = useState(() => readProgress(userId).skippedIds);
  const [sessionDone, setSessionDone] = useState(() => readProgress(userId).doneCount);
  const [trackedUser, setTrackedUser] = useState(userId);
  const [toast, setToast] = useState("");
  const [notice, setNotice] = useState("");
  const [deskLead, setDeskLead] = useState(null);
  const [goal, setGoal] = useState(null);

  if (userId !== trackedUser) {
    const saved = readProgress(userId);
    setTrackedUser(userId);
    setDoneIds(saved.doneIds);
    setSkippedIds(saved.skippedIds);
    setSessionDone(saved.doneCount);
  }

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const [pendingRes, leadsRes, goalsRes] = await Promise.all([
        crmService.getPending(),
        crmService.getLeads({ page: 1, limit: 1000, isActive: true, sortBy: "potentialValue", sortDir: "desc" }),
        crmService.getGoals().catch(() => null),
      ]);
      setPending(normalizePending(pendingRes.data?.data));
      setLeads(leadsRes.data?.data || []);
      const board = goalsRes?.data?.data;
      if (board?.me?.goalOn === false) setGoal(null);
      else if (board?.me) setGoal({ done: board.me.done, target: board.me.target || board.weeklyIndividualTarget });
    } catch {
      setPending(emptyPending());
      setLeads([]);
    } finally {
      setLoading(false);
      setBooted(true);
    }
  }, [enabled]);

  useEffect(() => {
    setBooted(false);
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    load();
    const onRefresh = () => load();
    window.addEventListener(NOTIFICATION_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATION_REFRESH_EVENT, onRefresh);
  }, [enabled, userId, load]);

  useEffect(() => {
    if (!userId) return;
    sessionStorage.setItem(
      progressStorageKey(userId),
      JSON.stringify({ date: todayKey(), doneIds, skippedIds, doneCount: sessionDone })
    );
  }, [userId, doneIds, skippedIds, sessionDone]);

  const queue = useMemo(() => buildDayQueue(pending, leads), [pending, leads]);
  const remaining = useMemo(
    () => queue.filter((item) => !doneIds.includes(item.id)),
    [queue, doneIds]
  );
  const active = useMemo(
    () => remaining.filter((item) => !skippedIds.includes(item.id)),
    [remaining, skippedIds]
  );
  const skipped = useMemo(
    () => remaining.filter((item) => skippedIds.includes(item.id)),
    [remaining, skippedIds]
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 1800);
    return () => clearTimeout(timer);
  }, [toast]);

  const markBriefSeen = useCallback(async () => {
    if (!isSeller) return;
    try {
      const res = await userService.ackDayBrief();
      updateUser({ dayBriefOn: res.data?.data?.dayBriefOn || todayKey() });
    } catch {
      /* si no se guarda, el aviso vuelve a salir al recargar */
    }
  }, [isSeller, updateUser]);

  useEffect(() => {
    if (!enabled || !isSeller || !booted) return;
    if (user?.dayBriefOn === todayKey()) return;
    setBriefOpen(true);
  }, [enabled, isSeller, booted, user?.dayBriefOn]);

  const openBrief = useCallback(() => setBriefOpen(true), []);
  const closeBrief = useCallback(() => {
    markBriefSeen();
    setBriefOpen(false);
  }, [markBriefSeen]);

  const startGuide = useCallback(() => {
    markBriefSeen();
    setBriefOpen(false);
    setGuideOpen(true);
    if (active.length === 0 && skipped.length > 0) setSkippedIds([]);
  }, [markBriefSeen, active.length, skipped.length]);

  const closeGuide = useCallback(() => setGuideOpen(false), []);

  const openCall = useCallback((lead) => {
    if (!lead?._id) return;
    setBriefOpen(false);
    setGuideOpen(false);
    setDeskLead(lead);
  }, []);

  const closeCall = useCallback(() => setDeskLead(null), []);

  const completeCurrent = useCallback((id) => {
    setDoneIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSkippedIds((prev) => prev.filter((item) => item !== id));
    setSessionDone((count) => count + 1);
    setToast("Listo. Siguiente.");
  }, []);

  const skipCurrent = useCallback((id) => {
    setSkippedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setToast("");
  }, []);

  const resumeSkipped = useCallback(() => {
    setSkippedIds([]);
    setGuideOpen(true);
  }, []);

  const settleLead = useCallback((id) => {
    const key = String(id || "");
    if (!key || !queue.some((item) => item.id === key) || doneIds.includes(key)) return;
    completeCurrent(key);
  }, [queue, doneIds, completeCurrent]);

  const resetDayBrief = useCallback(async () => {
    try {
      await userService.resetDayBrief();
      setNotice("Aviso reiniciado. El equipo lo verá al entrar, como si no hubiera empezado el día.");
    } catch (err) {
      setNotice(err?.response?.data?.message || "No se pudo reiniciar el aviso");
    }
  }, []);

  const logFollowUp = useCallback(async (id, payload) => {
    await crmService.addFollowUp(id, payload);
    completeCurrent(id);
    requestNotificationRefresh();
    requestCrmDataRefresh();
  }, [completeCurrent]);

  const value = useMemo(() => ({
    enabled,
    canReset,
    loading,
    queue,
    remaining,
    active,
    skipped,
    doneCount: sessionDone,
    briefOpen,
    guideOpen,
    toast,
    clearToast: () => setToast(""),
    openBrief,
    closeBrief,
    startGuide,
    closeGuide,
    openCall,
    completeCurrent,
    skipCurrent,
    resumeSkipped,
    settleLead,
    logFollowUp,
    resetDayBrief,
    goal,
  }), [
    enabled, canReset, loading, queue, remaining, active, skipped, sessionDone, goal,
    briefOpen, guideOpen, toast, openBrief, closeBrief, startGuide,
    closeGuide, openCall, completeCurrent, skipCurrent, resumeSkipped, settleLead, logFollowUp, resetDayBrief,
  ]);

  return (
    <DayCoachContext.Provider value={value}>
      {children}
      {deskLead && (
        <Suspense fallback={null}>
          <CallDesk
            lead={deskLead}
            onClose={closeCall}
            onSaved={closeCall}
          />
        </Suspense>
      )}
      <Snackbar
        open={Boolean(notice)}
        message={notice}
        autoHideDuration={3200}
        onClose={() => setNotice("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DayCoachContext.Provider>
  );
}

export function useDayCoach() {
  return useContext(DayCoachContext);
}
